// THE MUSIC PLAYER — two looping soundtracks that fade in, fade out and cross-fade; a master volume and a mute.
//
//   createMusicPlayer({ tracks, makeAudio, storage, now }) → { start, setActive, setMode, setVolume, setMuted, step, state, ... }
//
// Browsers do not allow sound before the player has touched the page, so nothing plays until start() (called on the first click or key).
// Mute only silences the output — and it fades over 2 s each way: both tracks keep their place and the fades carry on, so unmuting lands on the right track.
// A track that has faded out is paused where it was. When a track is brought in because the music SWITCHED (or the very first time), it
// starts at a RANDOM place in the song (never the last 25 s) and just carries on from there — so it never opens on bar one. Coming back to
// the game page after leaving it carries on exactly where the track was.
// `step(ms)` moves the fades along (the page calls it every 50 ms); everything else is instant. No timers in here, so tests drive it exactly.

export const FADE_IN_MS = 2500    // first start, and coming back to the game page
export const CROSSFADE_MS = 2000  // relax ⇄ juice
export const FADE_OUT_MS = 800    // leaving the game page / hiding the tab
export const MUTE_FADE_MS = 2000  // mute / unmute: the sound eases out (or back in) instead of snapping
export const SEEK_MARGIN_S = 25   // a random start never lands in the last 25 seconds of a song

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0))

export function createMusicPlayer({ tracks, makeAudio = (src) => new Audio(src), storage = null, random = Math.random } = {}) {
  const saved = (() => { try { return JSON.parse(storage?.getItem('mao-kingdom/music/v1') || 'null') || {} } catch { return {} } })()
  let volume = saved.volume == null ? 0.5 : clamp01(saved.volume)
  let muted = !!saved.muted
  let muteLevel = muted ? 0 : 1 // the mute fade: 1 = full sound, 0 = silent (eases toward the goal set by `muted`)
  let started = false      // the player has touched the page: sound is allowed
  let active = false       // the game page is showing (and the tab is visible)
  let mode = 'relax'
  let needsGesture = false // the browser refused to play: try again on the next touch
  const t = {}             // track name → { el, gain (the fade level 0…1), goal (0 or 1), rate (gain per ms), fresh (starts at a random place next time), retire (why it is fading out) }
  for (const name of Object.keys(tracks)) t[name] = { el: null, gain: 0, goal: 0, rate: 1 / FADE_IN_MS, fresh: true, retire: null }

  const save = () => { try { storage?.setItem('mao-kingdom/music/v1', JSON.stringify({ volume, muted })) } catch { /* private mode */ } }
  const apply = (tr) => { if (!tr.el) return; tr.el.volume = clamp01(tr.gain * volume * muteLevel); tr.el.muted = muted && muteLevel === 0 } // (truly muted only once the fade-out has finished)
  // jump to a random place in the song (waits for the song's length to be known if it is not yet)
  const seekRandom = (tr) => {
    const el = tr.el
    const go = () => { const d = el.duration; if (Number.isFinite(d) && d > SEEK_MARGIN_S * 2) { try { el.currentTime = random() * (d - SEEK_MARGIN_S) } catch { /* not seekable yet: it plays from the start */ } } }
    if (el.readyState >= 1 && Number.isFinite(el.duration)) go()
    else el.addEventListener?.('loadedmetadata', go, { once: true })
  }
  const play = (tr) => {
    if (!tr.el || !tr.el.paused) return
    if (tr.fresh) { tr.fresh = false; seekRandom(tr) }
    const p = tr.el.play?.()
    if (p?.catch) p.catch(() => { needsGesture = true })
  }
  function aim() { // set each track's goal from the mode and whether the music should be sounding at all
    const sounding = started && active
    for (const [name, tr] of Object.entries(t)) {
      const want = sounding && name === mode ? 1 : 0
      if (want !== tr.goal) {
        const otherPlaying = Object.entries(t).some(([n, o]) => n !== name && o.gain > 0)
        // a cross-fade when one track hands over to the other; a plain fade when the music as a whole starts or stops
        tr.goal = want; tr.rate = 1 / (want ? (otherPlaying ? CROSSFADE_MS : FADE_IN_MS) : (sounding ? CROSSFADE_MS : FADE_OUT_MS))
        tr.retire = want ? null : (sounding ? 'switch' : 'idle') // handed over to the other track, or the music as a whole stopped
      }
      if (want && tr.el) play(tr)
    }
  }

  const api = {
    // the first touch: create the two players and begin (relax fades in)
    start() {
      if (started) { if (needsGesture) { needsGesture = false; for (const tr of Object.values(t)) if (tr.goal) play(tr) } return }
      started = true
      for (const [name, src] of Object.entries(tracks)) { const el = makeAudio(src); el.loop = true; el.preload = 'auto'; t[name].el = el; apply(t[name]) }
      aim()
    },
    // the game page is showing and the tab is visible (or not): fade in / out
    setActive(on) { if (!!on === active) return; active = !!on; aim() },
    setMode(m) { if (!(m in t) || m === mode) return; mode = m; aim() },
    setVolume(v) { volume = clamp01(v); for (const tr of Object.values(t)) apply(tr); save() },
    setMuted(m) { muted = !!m; for (const tr of Object.values(t)) apply(tr); save() },
    // move the fades along by `ms`
    step(ms) {
      const wantLevel = muted ? 0 : 1
      const levelMoved = muteLevel !== wantLevel
      if (levelMoved) { const d = ms / MUTE_FADE_MS; muteLevel = wantLevel > muteLevel ? Math.min(1, muteLevel + d) : Math.max(0, muteLevel - d) }
      for (const tr of Object.values(t)) {
        if (tr.gain !== tr.goal) {
          const d = tr.rate * ms
          tr.gain = tr.goal > tr.gain ? Math.min(tr.goal, tr.gain + d) : Math.max(tr.goal, tr.gain - d)
          apply(tr)
          if (tr.gain === 0 && tr.goal === 0 && tr.el && !tr.el.paused) { tr.el.pause(); if (tr.retire === 'switch') tr.fresh = true } // (after a switch it comes back at a new random place)
        } else if (levelMoved) apply(tr)
      }
    },
    elements: () => Object.fromEntries(Object.entries(t).map(([n, tr]) => [n, tr.el])), // (for tests: the real audio elements)
    state: () => ({ started, active, mode, volume, muted, muteLevel, needsGesture, tracks: Object.fromEntries(Object.entries(t).map(([n, tr]) => [n, { gain: tr.gain, goal: tr.goal, playing: !!tr.el && !tr.el.paused, volume: tr.el ? tr.el.volume : 0 }])) }),
  }
  return api
}
